// frontend/src/components/FastReact/SetupFiosNavision.jsx
import React, { useState, useEffect, useRef } from 'react';
import { fastReactAPI } from '../../api/client';
import { FiPlay, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiClock, FiPackage, FiList } from 'react-icons/fi';
import './SetupFiosNavision.css';

const SetupFiosNavision = () => {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('run'); // 'run' | 'items' | 'codes'
  const esRef = useRef(null);
  const logEndRef = useRef(null);

  // ── Preparation tab state ───────────────────────────────────────────────────
  const [poSetups, setPoSetups]         = useState([]);
  const [loadingSetups, setLoadingSetups] = useState(false);
  const [expandedSetup, setExpandedSetup] = useState(null);
  const [setupItems, setSetupItems]     = useState({});  // { [po_setup_id]: items[] }
  const [showCompleted, setShowCompleted] = useState(false);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [progress]);

  // Load preview on mount and after run
  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await fastReactAPI.previewSetupFiosNavision();
      if (r.data.success) setPreviewData(r.data);
    } catch (e) {
      console.error('Preview error:', e);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPoSetups = async (withCompleted = showCompleted) => {
    setLoadingSetups(true);
    try {
      const r = await fastReactAPI.getPoSetups(withCompleted);
      if (r.data.success) setPoSetups(r.data.setups);
    } catch (e) { console.error('getPoSetups error:', e); }
    finally { setLoadingSetups(false); }
  };

  const loadSetupItems = async (poSetupId) => {
    if (setupItems[poSetupId]) return;
    try {
      const r = await fastReactAPI.getPreparationItems(poSetupId);
      if (r.data.success)
        setSetupItems(prev => ({ ...prev, [poSetupId]: r.data.items }));
    } catch (e) { console.error('getPreparationItems error:', e); }
  };

  const handleExpandSetup = (poSetupId) => {
    if (expandedSetup === poSetupId) { setExpandedSetup(null); return; }
    setExpandedSetup(poSetupId);
    loadSetupItems(poSetupId);
  };

  const handleTogglePrepared = async (poSetupId, preparationId) => {
    try {
      const r = await fastReactAPI.togglePrepared(preparationId);
      if (!r.data.success) return;
      const { is_prepared, is_completed, prepared_count, total_count } = r.data;
      setSetupItems(prev => ({
        ...prev,
        [poSetupId]: (prev[poSetupId] || []).map(item =>
          item.preparation_id === preparationId
            ? { ...item, is_prepared, prepared_at: is_prepared ? new Date().toISOString() : null }
            : item
        ),
      }));
      setPoSetups(prev =>
        prev.map(s =>
          s.po_setup_id === poSetupId
            ? { ...s, prepared_items: prepared_count, total_items: total_count, is_completed }
            : s
        )
      );
    } catch (e) { console.error('togglePrepared error:', e); }
  };

  useEffect(() => {
    if (activeTab === 'setups') loadPoSetups(showCompleted);
  }, [activeTab, showCompleted]);

  const handleRun = () => {
    if (running) return;

    setRunning(true);
    setProgress([]);
    setResult(null);
    setError(null);

    const url = fastReactAPI.getSetupFiosNavisionUrl();
    console.log('[SetupFiosNavision] Opening SSE:', url);

    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('progress', (e) => {
      const data = JSON.parse(e.data);
      console.log('[SSE progress]', data);
      setProgress((prev) => [...prev, { type: 'progress', ...data }]);
    });

    es.addEventListener('done', (e) => {
      const data = JSON.parse(e.data);
      console.log('[SSE done]', data);
      setResult(data);
      setRunning(false);
      es.close();
      loadPreview();
      setSetupItems({});
      loadPoSetups(showCompleted);
      setActiveTab('codes');
    });

    es.addEventListener('error', (e) => {
      console.error('[SSE error]', e);
      let msg = 'Erro desconhecido na ligação SSE.';
      try {
        const d = JSON.parse(e.data);
        msg = d.message || msg;
      } catch {}
      setError(msg);
      setRunning(false);
      es.close();
    });

    es.onerror = (e) => {
      console.error('[SSE onerror]', e);
      if (!running) return; // already closed
      setError('Ligação SSE interrompida.');
      setRunning(false);
      es.close();
    };
  };

  const handleStop = () => {
    esRef.current?.close();
    setRunning(false);
    setProgress((prev) => [
      ...prev,
      { type: 'progress', message: '⚠️ Processo interrompido pelo utilizador.' },
    ]);
  };

  const stepIcon = (step) => ['', '📥', '🔗', '💾', '🏷️', '🧮', '✅'][step] || '•';

  return (
    <div className="sfn-page">
      <div className="page-header page-header--compact">
        <h2>Setup Fios — Navision</h2>
      </div>

      <div className="sfn-tabs">
        {[
          { key: 'run',    label: '▶ Execução' },
          { key: 'items',  label: `🏷️ Items (${previewData?.items?.length ?? '…'})` },
          { key: 'codes',  label: `🧮 Códigos (${previewData?.codes?.length ?? '…'})` },
          { key: 'setups', label: `⚙️ Preparação (${poSetups.filter(s => s.status === 'SETUP' && !s.is_completed).length})` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            className={`sfn-tab ${activeTab === t.key ? 'sfn-tab--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}

        <button
          type="button"
          className="sfn-tab-refresh"
          onClick={loadPreview}
          disabled={loadingPreview}
          title="Atualizar dados"
        >
          <FiRefreshCw size={14} className={loadingPreview ? 'sfn-spin' : ''} />
        </button>
      </div>

      {activeTab === 'run' && (
        <div className="sfn-run-panel">
          <div className="sfn-actions">
            {!running ? (
              <button type="button" className="btn btn-primary sfn-run-btn" onClick={handleRun}>
                <FiPlay size={16} /> Executar Setup Fios
              </button>
            ) : (
              <button type="button" className="btn btn-danger sfn-run-btn" onClick={handleStop}>
                ⏹ Parar
              </button>
            )}
          </div>

          <div className="sfn-legend">
            {[
              'Navision → Reservas',
              'Items únicos + Códigos',
              'FR_IMPETUS → Strips',
              'Atualizar id_fios',
              'Análise de setup',
              'Ordens terminadas (Navision)',
            ].map((s, i) => (
              <div key={i} className="sfn-legend-item">
                <span>{stepIcon(i + 1)}</span>
                <span>{s}</span>
              </div>
            ))}
          </div>

          {(progress.length > 0 || running) && (
            <div className="sfn-log">
              {progress.map((p, i) => (
                <div key={i} className={`sfn-log-line ${p.type === 'error' ? 'sfn-log-line--error' : ''}`}>
                  <span className="sfn-log-step">{p.step ? stepIcon(p.step) : '•'}</span>
                  <span>{p.message}</span>
                </div>
              ))}
              {running && (
                <div className="sfn-log-line sfn-log-line--loading">
                  <span className="sfn-spinner">⏳</span> A processar…
                </div>
              )}
              <div ref={logEndRef} />
            </div>
          )}

          {result && (
            <div className={`sfn-result ${result.success ? 'sfn-result--ok' : 'sfn-result--error'}`}>
              {result.success ? (
                <>
                  <FiCheckCircle size={18} /> {result.message}
                </>
              ) : (
                <>
                  <FiAlertCircle size={18} /> {result.message}
                </>
              )}

              {result.success && (
                <div className="sfn-result-stats">
                  <span>🔗 {result.rows} reservas</span>
                  <span>🏷️ {result.items} items únicos</span>
                  <span>🧮 {result.codes} códigos</span>
                  <span>📥 {result.strips} strips</span>
                  <span>⚙️ {result.setups ?? 0} setup(s)</span>
                  <span>✅ {result.finished ?? 0} terminada(s)</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="sfn-result sfn-result--error">
              <FiAlertCircle size={18} /> {error}
            </div>
          )}

          {previewData && (
            <div className="sfn-last-run">
              <FiClock size={12} />
              {previewData.codes?.length > 0
                ? `Última execução: ${new Date(previewData.codes[0]?.created_at).toLocaleString('pt-PT')}`
                : 'Sem dados de execução anterior.'}
            </div>
          )}
        </div>
      )}

      {activeTab === 'items' && (
        <div className="sfn-table-panel">
          <h3>Items Únicos (item_variant_lot)</h3>

          {loadingPreview ? (
            <p className="sfn-loading">A carregar…</p>
          ) : !previewData?.items?.length ? (
            <p className="sfn-empty">Sem dados. Execute o Setup Fios primeiro.</p>
          ) : (
            <div className="sfn-table-wrap">
              <table className="sfn-table sfn-table--items">
                <colgroup>
                  <col style={{ width: '56px' }} />
                  <col style={{ width: '110px' }} />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Índice</th>
                    <th>Item / Variante / Lote</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.items.map((item, i) => (
                    <tr key={item.item_id || i}>
                      <td className="sfn-td-num">{i + 1}</td>
                      <td className="sfn-td-idx">{item.item_index}</td>
                      <td className="sfn-td-ivl">{item.item_variant_lot}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'codes' && (
        <div className="sfn-table-panel">
          <h3>Códigos de Setup</h3>

          {loadingPreview ? (
            <p className="sfn-loading">A carregar…</p>
          ) : !previewData?.codes?.length ? (
            <p className="sfn-empty">Sem dados. Execute o Setup Fios primeiro.</p>
          ) : (
            <div className="sfn-table-wrap">
              <table className="sfn-table sfn-table--codes">
                <colgroup>
                  <col style={{ width: '56px' }} />
                  <col style={{ width: '180px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '170px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código Setup</th>
                    <th>Índices</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.codes.map((code, i) => (
                    <tr key={code.code_id || i}>
                      <td className="sfn-td-num">{i + 1}</td>
                      <td className="sfn-td-code">{code.setup_code}</td>
                      <td className="sfn-td-idx">{code.setup_indices}</td>
                      <td className="sfn-td-date">
                        {code.created_at ? new Date(code.created_at).toLocaleString('pt-PT') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeTab === 'setups' && (
        <div className="sfn-table-panel">
          <div className="sfn-setups-header">
            <h3><FiPackage size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Preparação de Setup</h3>
            <label className="sfn-toggle-completed">
              <input type="checkbox" checked={showCompleted}
                onChange={e => setShowCompleted(e.target.checked)} />
              Mostrar concluídos
            </label>
            <button type="button" className="sfn-tab-refresh"
              onClick={() => loadPoSetups(showCompleted)} disabled={loadingSetups}
              title="Atualizar">
              <FiRefreshCw size={14} className={loadingSetups ? 'sfn-spin' : ''} />
            </button>
          </div>

          {loadingSetups ? (
            <p className="sfn-loading">A carregar…</p>
          ) : !poSetups.length ? (
            <p className="sfn-empty">
              {showCompleted
                ? 'Sem registos de preparação.'
                : 'Sem setups pendentes. Execute o cálculo para detetar alterações.'}
            </p>
          ) : (
            <div className="sfn-setups-list">
              {poSetups.map(setup => {
                const isExp = expandedSetup === setup.po_setup_id;
                const items = setupItems[setup.po_setup_id] || [];
                const pct   = setup.total_items > 0
                  ? Math.round((setup.prepared_items / setup.total_items) * 100) : 0;
                return (
                  <div key={setup.po_setup_id}
                    className={`sfn-setup-card ${setup.is_completed ? 'sfn-setup-card--done' : ''} ${setup.status === 'SETUP' ? 'sfn-setup-card--setup' : ''}`}>

                    <div className="sfn-setup-card-header"
                      onClick={() => handleExpandSetup(setup.po_setup_id)}>
                      <div className="sfn-setup-card-left">
                        {setup.linha && (
                          <span className="sfn-setup-linha">{setup.linha}</span>
                        )}
                        <span className="sfn-setup-code">{setup.po_code_line_full}</span>
                        {setup.status === 'SETUP' && !setup.is_completed && (
                          <span className="sfn-status-badge sfn-status-badge--setup">SETUP</span>
                        )}
                        {setup.is_completed && (
                          <span className="sfn-status-badge sfn-status-badge--done">
                            <FiCheckCircle size={10}/> concluído
                          </span>
                        )}
                        {setup.product_name && (
                          <span className="sfn-setup-product">{setup.product_name}</span>
                        )}
                        {setup.customer_name && (
                          <span className="sfn-setup-customer">{setup.customer_name}</span>
                        )}
                        {setup.strip_start && (
                          <span className="sfn-setup-date">
                            {new Date(setup.strip_start).toLocaleDateString('pt-PT')}
                          </span>
                        )}
                        {setup.indices && (
                          <span className="sfn-setup-indices" title="Índices de setup">
                            <FiList size={10}/> {setup.indices}
                          </span>
                        )}
                      </div>
                      <div className="sfn-setup-card-right">
                        <div className="sfn-progress-bar">
                          <div className="sfn-progress-fill" style={{ width: `${pct}%` }}/>
                        </div>
                        <span className="sfn-progress-label">
                          {setup.prepared_items}/{setup.total_items}
                        </span>
                        <span className={`sfn-chevron ${isExp ? 'sfn-chevron--open' : ''}`}>▾</span>
                      </div>
                    </div>

                    {isExp && (
                      <div className="sfn-setup-items">
                        {!items.length ? (
                          <p className="sfn-loading">A carregar itens…</p>
                        ) : (
                          <table className="sfn-table sfn-table--prep">
                            <thead>
                              <tr>
                                <th style={{ width:36 }}></th>
                                <th>Produto</th>
                                <th>Variante</th>
                                <th>Lote</th>
                                <th style={{ textAlign:'right' }}>Qtd</th>
                                <th>Estado</th>
                                <th>Preparado por</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map(item => (
                                <tr key={item.preparation_id}
                                  className={item.is_prepared ? 'sfn-item-prepared' : ''}>
                                  <td>
                                    <input type="checkbox" className="sfn-prep-check"
                                      checked={item.is_prepared}
                                      onChange={() => handleTogglePrepared(setup.po_setup_id, item.preparation_id)}/>
                                  </td>
                                  <td>
                                    <span className="sfn-td-code">{item.product_code}</span>
                                    {item.product_description && (
                                      <span className="sfn-td-desc">{item.product_description}</span>
                                    )}
                                  </td>
                                  <td>{item.variant_code || '—'}</td>
                                  <td>{item.lot_no}</td>
                                  <td style={{ textAlign:'right' }}>
                                    {item.quantity != null ? parseFloat(item.quantity).toFixed(0) : '—'}
                                  </td>
                                  <td>
                                    {item.status_line
                                      ? <span className="sfn-status-badge sfn-status-badge--setup">{item.status_line}</span>
                                      : '—'}
                                  </td>
                                  <td>
                                    {item.is_prepared
                                      ? (item.prepared_by_name || '—')
                                      : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default SetupFiosNavision;